import { type Location, Navigate, Outlet, type RouteObject, useLocation, useRoutes } from "react-router";
import type { ApiClient } from "./api/client";
import { type AppConfig, ConfigProvider, DEFAULT_CONFIG } from "./app/config";
import { AccessKeysPage } from "./pages/admin/AccessKeysPage";
import { AdminLayout } from "./pages/admin/AdminLayout";
import { OpsPage } from "./pages/admin/OpsPage";
import { TagPoliciesPage } from "./pages/admin/TagPoliciesPage";
import { UsersPage } from "./pages/admin/UsersPage";
import { BookmarkPage, BookmarksPage, LegacyFavorites } from "./pages/BookmarksPage";
import { BrowsePage } from "./pages/BrowsePage";
import { DownloadsPage } from "./pages/DownloadsPage";
import { GalleryWindow } from "./pages/GalleryWindow";
import { HistoryPage } from "./pages/HistoryPage";
import { LoginPage } from "./pages/LoginPage";
import { ReaderPage } from "./pages/ReaderPage";
import { SettingsPage } from "./pages/SettingsPage";
import { FavoritesProvider } from "./personal/favorites";
import { AckProvider } from "./policy/ack";
import { ServerBlocksProvider } from "./policy/blocked";
import { SessionProvider, useSession } from "./session";
import { GallerySourceProvider, useGalleryVisits } from "./ui/GallerySource";
import { AppShell } from "./ui/Shell";
import { ErrorNote, Loading } from "./ui/States";
import { AdminVideosPage, MinePage } from "./videos/ManagePages";
import { SeriesListPage, SeriesPage, VideoHomePage, VideoListPage, WatchPage } from "./videos/pages";
import { UploadPage } from "./videos/UploadPage";

function RequireSession() {
  const { state, retry } = useSession();
  const location = useLocation();
  if (state.status === "loading") return <Loading />;
  if (state.status === "unavailable") return <ErrorNote error={state.error} onRetry={retry} />;
  if (state.status === "anonymous")
    return <Navigate to="/login" replace state={{ from: `${location.pathname}${location.search}` }} />;
  // keyed by account: warning confirmations and the favourites index never carry over to another login
  return (
    <AckProvider key={state.user.id}>
      <ServerBlocksProvider>
        <FavoritesProvider>
          <GallerySourceProvider>
            <Outlet />
          </GallerySourceProvider>
        </FavoritesProvider>
      </ServerBlocksProvider>
    </AckProvider>
  );
}

function Root({ client, config }: { client: ApiClient; config: AppConfig }) {
  return (
    <ConfigProvider value={config}>
      <SessionProvider client={client}>
        <Outlet />
      </SessionProvider>
    </ConfigProvider>
  );
}

function ContentRoutes() {
  const location = useLocation();
  // Lives above the reader route, so returning from reading retains the modal's origin.
  const visited = useGalleryVisits();
  if (!location.pathname.startsWith("/g/")) visited.add(location.key);
  const candidate = (location.state as { background?: Location } | null)?.background;
  const background = candidate && visited.has(candidate.key) ? candidate : undefined;
  const match = /^\/g\/([^/]+)$/.exec(location.pathname);
  const routes = useRoutes(
    [
      { path: "/", element: <BrowsePage /> },
      { path: "/favorites", element: <LegacyFavorites /> },
      { path: "/bookmarks", element: <BookmarksPage /> },
      { path: "/bookmarks/:id", element: <BookmarkPage /> },
      { path: "/history", element: <HistoryPage /> },
      { path: "/downloads", element: <DownloadsPage /> },
      { path: "/settings", element: <SettingsPage /> },
      { path: "/videos", element: <VideoHomePage /> },
      { path: "/videos/list", element: <VideoListPage /> },
      { path: "/videos/saved", element: <VideoListPage saved /> },
      { path: "/videos/mine", element: <MinePage /> },
      { path: "/videos/upload", element: <UploadPage /> },
      { path: "/videos/series", element: <SeriesListPage /> },
      { path: "/videos/s/:id", element: <SeriesPage /> },
      { path: "/videos/:id", element: <WatchPage /> },
      {
        path: "/admin",
        element: <AdminLayout />,
        children: [
          { index: true, element: <Navigate to="/admin/tags" replace /> },
          { path: "tags", element: <TagPoliciesPage /> },
          { path: "videos", element: <AdminVideosPage /> },
          { path: "users", element: <UsersPage /> },
          { path: "keys", element: <AccessKeysPage /> },
          { path: "ops", element: <OpsPage /> },
          // the former sync and all-downloads tabs are part of ops now (Spec M-1)
          { path: "sync", element: <Navigate to="/admin/ops" replace /> },
          { path: "downloads", element: <Navigate to="/admin/ops" replace /> },
        ],
      },
      { path: "*", element: <Navigate to="/" replace /> },
    ],
    match ? (background ?? { ...location, pathname: "/" }) : location,
  );
  return (
    <>
      {routes}
      {match && <GalleryWindow gidParam={match[1]!} inApp={background !== undefined} />}
    </>
  );
}
export function appRoutes(client: ApiClient, config: Partial<AppConfig> = {}): RouteObject[] {
  return [
    {
      element: <Root client={client} config={{ ...DEFAULT_CONFIG, ...config }} />,
      children: [
        { path: "/login", element: <LoginPage /> },
        {
          element: <RequireSession />,
          children: [
            { element: <AppShell />, children: [{ path: "*", element: <ContentRoutes /> }] },
            { path: "/read/:gid", element: <ReaderPage /> },
          ],
        },
      ],
    },
  ];
}
